import { breadcrumbSchema } from "@/lib/schema";
import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/Layout";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";
import { CTAButton } from "@/components/site/Button";
import { VisibilityAuditWidget } from "@/components/site/VisibilityAuditWidget";
import { BRAND } from "@/lib/brand";

export const Route = createFileRoute("/resources")({
  head: () => ({
    meta: [
      { title: "Real Estate Agent Resources - Audit and COLE Method" },
      {
        name: "description",
        content:
          "Free real estate agent resources from Kristan Cole: an online visibility audit, The COLE Method framework, and tools to grow your business this year.",
      },
      { property: "og:title", content: "Resources - Kristan Cole Network" },
      {
        property: "og:description",
        content:
          "Free visibility audit and The COLE Method framework for growing agents.",
      },
      { property: "og:url", content: "https://joinkcn.com/resources" },
    ],
    links: [
      { rel: "canonical", href: "https://joinkcn.com/resources" },
      { rel: "preconnect", href: "https://services.leadconnectorhq.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://services.leadconnectorhq.com" },
      { rel: "preload", as: "script", href: "https://services.leadconnectorhq.com/prospecting/client/widget-embed.js", crossOrigin: "anonymous" },
    ],
    scripts: [breadcrumbSchema([{ name: "Resources", path: "/resources" }])],
  }),
  component: ResourcesPage,
});

function ResourcesPage() {
  return (
    <SiteLayout>
      <section className="pt-36 pb-14 border-b border-border">
        <div className="container-edge">
          <p className="eyebrow mb-6">Resources</p>
          <h1 className="headline-xl max-w-5xl">
            Tools to grow your
            <br />
            <span className="serif-italic text-primary">visibility & legacy.</span>
          </h1>
        </div>
      </section>

      {/* Primary Resource - Visibility Audit */}
      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <SectionEyebrow>Flagship Resource - Free Instant Audit</SectionEyebrow>
            <h2 className="headline-lg mb-6">
              Free Online
              <br />
              <span className="serif-italic text-primary">Visibility Audit.</span>
            </h2>
            <p className="text-foreground/90 text-lg leading-relaxed mb-6">
              Before you invest in more marketing, find out exactly where your
              online presence stands. In minutes, see how buyers and sellers are
              finding you right now - and whether AI is citing you in the new
              digital age.
            </p>
            <p className="text-foreground/80 leading-relaxed mb-8">
              We uncover the parts of your digital foundation that decide
              whether Google ranks you and whether ChatGPT, Gemini, and Perplexity
              recommend you.
            </p>
            <ul className="space-y-3 border-t border-border/60 pt-6 mb-8">
              {[
                "Complete SEO and online visibility analysis",
                "Reputation and review-rating breakdown",
                "Local listings accuracy across 50+ directories",
                "AI citation & answer-engine readiness",
                "Competitive positioning in your market",
              ].map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-foreground/90"
                >
                  <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            id="audit-form"
            className="lg:col-span-5 border border-border bg-surface p-8"
          >
            <p className="eyebrow mb-3">Get Your Free Audit</p>
            <h3 className="font-display text-2xl mb-4">
              Instant Marketing Visibility Report
            </h3>
            <VisibilityAuditWidget />
          </div>
        </div>
      </Section>




      <Section tone="dark">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow>Follow along</SectionEyebrow>
            <h2 className="headline-lg">
              The real
              <br />
              <span className="serif-italic text-primary">working classroom.</span>
            </h2>
            <p className="mt-6 text-foreground/85 text-lg">
              Most of Kristan's teaching lives where her team lives: in the
              Monthly Market Round Table. Everything else is signal you can
              follow in between.
            </p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="divide-y divide-border border-y border-border">
              {[
                ["Instagram", BRAND.socials.instagram],
                ["YouTube", BRAND.socials.youtube],
                ["LinkedIn", BRAND.socials.linkedin],
                ["Facebook", BRAND.socials.facebook],
              ].map(([label, url]) => (
                <li key={label}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between py-5 group"
                  >
                    <span className="font-display text-2xl text-foreground">
                      {label}
                    </span>
                    <span className="text-foreground/40 group-hover:text-primary transition-colors">
                      →
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <CTABanner cta="roundtable" eyebrow="Ready for more?" />
    </SiteLayout>
  );
}
